import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Check, 
  X, 
  Users, 
  Upload, 
  Eye, 
  Calendar, 
  BookOpen, 
  Phone, 
  Mail, 
  MapPin, 
  Briefcase, 
  Clock, 
  CheckCircle2, 
  AlertTriangle,
  Loader2,
  Key,
  GraduationCap,
  Award,
  ShieldCheck,
  UserCheck,
  Search,
  Pencil,
  Star
} from 'lucide-react';
import { api } from '../../api/client';
import { showToast } from '../../components/common/Toast';
import Modal from '../../components/common/Modal';
import PasswordInput from '../../components/common/PasswordInput';
import { useAuth } from '../../context/AuthContext';
import { formatUzPhone, capitalizeWords, formatDate } from '../../utils/helpers';

export default function AdminLawyersPage() {
  const { user } = useAuth();
  const isLawyer = user?.role === 'LAWYER';
  const currentLawyerId = user?.lawyerId;

  const [lawyers, setLawyers] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLawyer, setSearchLawyer] = useState('');

  // Edit / Add Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLawyer, setEditingLawyer] = useState(null);
  const [saving, setSaving] = useState(false);

  // Specialized Lawyer Account Modal
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [accountLawyer, setAccountLawyer] = useState(null);
  const [savingAccount, setSavingAccount] = useState(false);
  const [accountForm, setAccountForm] = useState({
    username: '',
    email: '',
    password: '',
    phone: '',
  });

  // Dossier Modal
  const [dossierLawyer, setDossierLawyer] = useState(null);
  const [isDossierOpen, setIsDossierOpen] = useState(false);
  const [dossierTab, setDossierTab] = useState('INFO'); // 'INFO', 'SCHEDULE', 'DIARY'

  // Schedule Overrides for selected lawyer
  const [overrides, setOverrides] = useState([]);
  const [newOverrideDate, setNewOverrideDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [newOverrideStatus, setNewOverrideStatus] = useState('COURT');
  const [newOverrideReason, setNewOverrideReason] = useState('Jizzax viloyat sudi, sud majlisi');
  const [savingOverride, setSavingOverride] = useState(false);

  // Court Diary / Notes for selected lawyer
  const [notes, setNotes] = useState([]);
  const [newNoteTitle, setNewNoteTitle] = useState('');
  const [newNoteLocation, setNewNoteLocation] = useState('Jizzax viloyat sudi');
  const [newNoteDate, setNewNoteDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [newNoteStartTime, setNewNoteStartTime] = useState('09:00');
  const [newNoteEndTime, setNewNoteEndTime] = useState('12:00');
  const [newNoteParties, setNewNoteParties] = useState('');
  const [newNoteContent, setNewNoteContent] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    position: '',
    specialization: '',
    education: '',
    certifications: '',
    experienceYears: 7,
    languages: "O'zbek, Rus, Ingliz",
    phone: '',
    email: '',
    telegram: '',
    instagram: '',
    linkedin: '',
    photoUrl: '',
    bioUz: '',
    bioRu: '',
    bioEn: '',
    serviceIds: [],
    isActive: true,
  });

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast('Faqat rasm fayllari (JPG, PNG, WEBP) qabul qilinadi', 'error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast('Rasm hajmi 10MB dan kichik bo‘lishi kerak', 'error');
      return;
    }

    // Immediately load FileReader preview so image is never blank
    const reader = new FileReader();
    reader.onload = (event) => {
      setFormData((prev) => ({ ...prev, photoUrl: event.target.result }));
    };
    reader.readAsDataURL(file);

    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('category', 'lawyers');

    setUploadingImage(true);
    try {
      const res = await api.postFormData('/upload', uploadData);
      if (res?.success && res?.url) {
        setFormData((prev) => ({ ...prev, photoUrl: res.url }));
        showToast('Fotosurat tiniq formatda muvaffaqiyatli yuklandi!', 'success');
      }
    } catch (err) {
      // Local preview already set
      showToast('Fotosurat tanlandi', 'info');
    } finally {
      setUploadingImage(false);
    }
  };

  const loadLawyers = () => {
    setLoading(true);
    api.get('/lawyers?includeInactive=true')
      .then((res) => {
        if (res?.success) setLawyers(res.lawyers || []);
      })
      .catch((err) => showToast(err.message, 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadLawyers();
    api.get('/services').then((res) => {
      if (res?.success) setServices(res.services || []);
    });
  }, []);

  const handleOpenAdd = () => {
    setEditingLawyer(null);
    setFormData({
      fullName: '',
      position: 'Katta Advokat',
      specialization: 'Jinoyat va Iqtisodiy Sud Himoyasi',
      education: 'Toshkent davlat yuridik universiteti (TDYuU) - Huquqshunoslik magistri',
      certifications: 'O‘zbekiston Respublikasi Advokatlar Palatasi a’zosi va davlat litsenziyasi',
      experienceYears: 7,
      languages: "O'zbek, Rus, Ingliz",
      phone: '+998 ()',
      email: '',
      telegram: 'https://t.me/sevinchadvokat',
      instagram: 'https://instagram.com/sevinchadvokat',
      linkedin: '',
      photoUrl: 'https://images.unsplash.com/photo-1556157382-97eda2d62296?auto=format&fit=crop&q=80&w=800',
      bioUz: 'Sevinch advokatlar hay’atining yetakchi advokati. Murakkab jinoyat va fuqarolik ishlari bo‘yicha amaliy tajribaga ega.',
      bioRu: 'Ведущий адвокат коллегии адвокатов Севинч с практическим опытом в уголовных и гражданских делах.',
      bioEn: 'Senior attorney at Sevinch Law Advocates with practical defense experience.',
      serviceIds: services.map((s) => s.id),
      isActive: true,
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (lawyer) => {
    setEditingLawyer(lawyer);
    setFormData({
      fullName: lawyer.fullName,
      position: lawyer.position,
      specialization: lawyer.specialization,
      education: lawyer.education || '',
      certifications: lawyer.certifications || '',
      experienceYears: lawyer.experienceYears,
      languages: lawyer.languages,
      phone: lawyer.phone ? formatUzPhone(lawyer.phone) : '+998 ()',
      email: lawyer.email || '',
      telegram: lawyer.telegram || '',
      instagram: lawyer.instagram || '',
      linkedin: lawyer.linkedin || '',
      photoUrl: lawyer.photoUrl || '',
      bioUz: lawyer.bioUz || '',
      bioRu: lawyer.bioRu || '',
      bioEn: lawyer.bioEn || '',
      serviceIds: (lawyer.services || []).map((s) => s.serviceId || s.service?.id),
      isActive: lawyer.isActive,
    });
    setIsModalOpen(true);
  };

  const handleOpenAccount = (lawyer) => {
    setAccountLawyer(lawyer);
    const suggestedUsername = (lawyer.user?.username) || (lawyer.slug ? lawyer.slug.replace(/-/g, '.') : lawyer.fullName.toLowerCase().replace(/[^a-z0-9]/g, ''));
    setAccountForm({
      username: suggestedUsername,
      email: lawyer.user?.email || lawyer.email || `${suggestedUsername}@system.local`,
      password: '',
      phone: lawyer.phone ? formatUzPhone(lawyer.phone) : '+998 ()',
    });
    setIsAccountModalOpen(true);
  };

  const handleSaveAccount = async (e) => {
    e.preventDefault();
    if (!accountLawyer) return;
    const cleanUser = accountForm.username.trim().toLowerCase().replace(/^@+/, '');
    if (!cleanUser) {
      showToast('Login (username) kiritilishi shart', 'error');
      return;
    }
    const hasAccount = !!(accountLawyer.user || accountLawyer.userId);
    if (!hasAccount && (!accountForm.password || accountForm.password.trim().length < 6)) {
      showToast('Yangi akkaunt uchun parol kamida 6 ta belgidan iborat bo‘lishi kerak', 'error');
      return;
    }

    setSavingAccount(true);
    try {
      const payload = {
        username: cleanUser,
        password: accountForm.password ? accountForm.password.trim() : undefined,
        phone: accountForm.phone && accountForm.phone !== '+998 ()' ? accountForm.phone.trim() : null,
        email: accountForm.email && accountForm.email.trim() ? accountForm.email.trim().toLowerCase() : `${cleanUser}@system.local`,
      };
      const res = await api.post(`/lawyers/${accountLawyer.id}/account`, payload);
      if (res?.success) {
        showToast(
          `Advokat akkaunti muvaffaqiyatli saqlandi! Login: @${cleanUser}`,
          'success'
        );
        setIsAccountModalOpen(false);
        loadLawyers();
      }
    } catch (err) {
      showToast(err.message || 'Akkaunt yaratishda xatolik', 'error');
    } finally {
      setSavingAccount(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.fullName || !formData.position) {
      showToast('Ism va lavozimni kiriting', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        isActive: Boolean(formData.isActive),
        specialization: formData.specialization?.trim() || 'Umumiy amaliyot',
        education: formData.education?.trim() || '',
        certifications: formData.certifications?.trim() || '',
        experienceYears: parseInt(formData.experienceYears, 10) || 1,
      };
      if (editingLawyer) {
        const res = await api.put(`/lawyers/${editingLawyer.id}`, payload);
        if (res?.success) {
          showToast(
            payload.isActive
              ? 'Advokat profili yangilandi va bosh sahifaga qo‘shildi'
              : 'Advokat profili yangilandi va bosh sahifadan olindi',
            'success'
          );
          setIsModalOpen(false);
          loadLawyers();
        }
      } else {
        const res = await api.post('/lawyers', payload);
        if (res?.success) {
          showToast(
            payload.isActive
              ? 'Yangi advokat muvaffaqiyatli qo‘shildi va bosh sahifaga chiqarildi'
              : 'Yangi advokat qo‘shildi (bosh sahifada yashirilgan)',
            'success'
          );
          setIsModalOpen(false);
          loadLawyers();
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Haqiqatan ham ${name} profilini o‘chirmoqchimisiz?`)) return;
    try {
      const res = await api.delete(`/lawyers/${id}`);
      if (res?.success) {
        showToast('Advokat profili o‘chirildi', 'success');
        loadLawyers();
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleToggleActive = async (lawyer) => {
    try {
      const res = await api.put(`/lawyers/${lawyer.id}/toggle-active`, {});
      if (res?.success) {
        showToast(
          res.message || (res.lawyer?.isActive ? 'Advokat bosh sahifaga qo‘shildi' : 'Advokat bosh sahifadan olindi'),
          'success'
        );
        loadLawyers();
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const toggleService = (sId) => {
    const sIdNum = parseInt(sId, 10);
    setFormData((prev) => {
      const exists = prev.serviceIds.includes(sIdNum);
      return {
        ...prev,
        serviceIds: exists ? prev.serviceIds.filter((id) => id !== sIdNum) : [...prev.serviceIds, sIdNum],
      };
    });
  };

  // Open Detailed Dossier View
  const handleOpenDossier = async (lawyer) => {
    setDossierLawyer(lawyer);
    setDossierTab('INFO');
    setIsDossierOpen(true);

    // Load overrides & notes
    try {
      const [oRes, nRes] = await Promise.all([
        api.get(`/lawyers/${lawyer.id}/schedule-overrides`),
        api.get(`/lawyers/${lawyer.id}/notes`),
      ]);
      if (oRes?.success) setOverrides(oRes.overrides || []);
      if (nRes?.success) setNotes(nRes.notes || []);
    } catch (err) {
      console.warn('Error loading dossier specifics:', err);
    }
  };

  // Add / Save Schedule Override
  const handleSaveScheduleOverride = async (e) => {
    e.preventDefault();
    if (!dossierLawyer) return;

    setSavingOverride(true);
    try {
      const res = await api.post(`/lawyers/${dossierLawyer.id}/schedule-overrides`, {
        date: newOverrideDate,
        status: newOverrideStatus,
        reason: newOverrideReason,
      });
      if (res?.success) {
        showToast('Jadval holati saqlandi!', 'success');
        const updated = await api.get(`/lawyers/${dossierLawyer.id}/schedule-overrides`);
        if (updated?.success) setOverrides(updated.overrides || []);
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSavingOverride(false);
    }
  };

  const handleDeleteOverride = async (overrideId) => {
    try {
      const res = await api.delete(`/lawyers/${dossierLawyer.id}/schedule-overrides/${overrideId}`);
      if (res?.success) {
        showToast('Holat bekor qilindi', 'info');
        setOverrides((prev) => prev.filter((o) => o.id !== overrideId));
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // Add Court Diary Note
  const handleSaveNote = async (e) => {
    e.preventDefault();
    if (!dossierLawyer || !newNoteTitle || !newNoteContent) {
      showToast('Sarlavha va matn kiritilishi shart', 'error');
      return;
    }

    const timeRange = newNoteEndTime 
      ? `${newNoteStartTime} dan ${newNoteEndTime} gacha`
      : newNoteStartTime;

    setSavingNote(true);
    try {
      const res = await api.post(`/lawyers/${dossierLawyer.id}/notes`, {
        title: newNoteTitle,
        location: newNoteLocation,
        eventDate: newNoteDate,
        eventTime: timeRange,
        parties: newNoteParties,
        content: newNoteContent,
      });
      if (res?.success) {
        showToast('Sud daftarchasiga yangi yozuv va eslatmalar qo‘shildi!', 'success');
        setNewNoteTitle('');
        setNewNoteParties('');
        setNewNoteContent('');
        const updated = await api.get(`/lawyers/${dossierLawyer.id}/notes`);
        if (updated?.success) setNotes(updated.notes || []);
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSavingNote(false);
    }
  };

  const handleToggleNoteDone = async (noteId, currentDone) => {
    try {
      const res = await api.put(`/lawyers/notes/${noteId}`, { isDone: !currentDone });
      if (res?.success) {
        setNotes((prev) =>
          prev.map((n) => (n.id === noteId ? { ...n, isDone: !currentDone } : n))
        );
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const res = await api.delete(`/lawyers/notes/${noteId}`);
      if (res?.success) {
        showToast('Yozuv o‘chirildi', 'info');
        setNotes((prev) => prev.filter((n) => n.id !== noteId));
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const getStatusBadgeProps = (status) => {
    switch (status) {
      case 'FREE':
        return { label: 'Bo‘sh (Qabul ochiq)', bg: 'bg-emerald-50 text-emerald-800 border-emerald-300' };
      case 'COURT':
        return { label: 'Sudi bor (Sud majlisi)', bg: 'bg-rose-50 text-rose-800 border-rose-300' };
      case 'BUSY':
        return { label: 'Ishi bor (Band)', bg: 'bg-amber-50 text-amber-800 border-amber-300' };
      case 'OFF':
        return { label: 'Dam olish kuni', bg: 'bg-slate-100 text-slate-800 border-slate-300' };
      default:
        return { label: status, bg: 'bg-slate-50 text-slate-700 border-slate-200' };
    }
  };

  const baseLawyers = isLawyer && currentLawyerId
    ? lawyers.filter((l) => l.id === currentLawyerId)
    : lawyers;

  const displayedLawyers = baseLawyers.filter((l) => {
    if (!searchLawyer.trim()) return true;
    const q = searchLawyer.toLowerCase();
    const digitsOnly = searchLawyer.replace(/\D/g, '');
    return (
      l.fullName?.toLowerCase().includes(q) ||
      l.specialization?.toLowerCase().includes(q) ||
      l.position?.toLowerCase().includes(q) ||
      l.email?.toLowerCase().includes(q) ||
      l.phone?.includes(q) ||
      (digitsOnly.length >= 3 && l.phone?.includes(digitsOnly))
    );
  });

  return (
    <div className="space-y-6 animate-admin-fade-in">
      {/* Action Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-lg font-bold text-slate-900">
            {isLawyer ? 'Mening Shaxsiy Dosyem va Sud Kundaligim' : `Ishchilar va Advokatlar Katalogi (${displayedLawyers.length})`}
          </h2>
          <p className="text-xs text-slate-500">
            {isLawyer 
              ? 'Shaxsiy advokatlik profili, sud daftarchasi, bandlik va ish jadvali' 
              : 'Jizzax Advokatlari xodimlari ro‘yxati, shaxsiy dosyelar, sud kundaligi va ish jadvali'}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="relative w-full sm:w-64">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Advokat, ixtisoslik, tel yoki email izlash..."
              value={searchLawyer}
              onChange={(e) => setSearchLawyer(e.target.value)}
              className="w-full pl-9 pr-8 py-2 text-xs bg-white border border-slate-300 rounded focus:border-[#c5a880] focus:outline-none"
            />
            {searchLawyer && (
              <button
                type="button"
                onClick={() => setSearchLawyer('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs font-bold"
              >
                ✕
              </button>
            )}
          </div>

          {!isLawyer && (
            <button
              onClick={handleOpenAdd}
              className="inline-flex items-center justify-center px-4 py-2 text-xs font-semibold text-[#091322] bg-[#c5a880] hover:bg-[#d4b993] rounded shadow-sm transition-colors cursor-pointer shrink-0"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Yangi advokat qo‘shish
            </button>
          )}
        </div>
      </div>

      {/* Lawyers Table */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-3 px-4">Surat</th>
                <th className="py-3 px-4">Advokat & Ta’lim</th>
                <th className="py-3 px-4">Lavozim & Ixtisoslashuv</th>
                <th className="py-3 px-4 whitespace-nowrap">Tajriba</th>
                <th className="py-3 px-4 whitespace-nowrap">Telefon</th>
                <th className="py-3 px-4 whitespace-nowrap">Akkaunt / Kirish</th>
                <th className="py-3 px-4 whitespace-nowrap">Holat / Bosh sahifa</th>
                <th className="py-3 px-4 text-right whitespace-nowrap min-w-[280px]">Amallar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-slate-400">Yuklanmoqda...</td>
                </tr>
              ) : displayedLawyers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-slate-400">Hozircha advokatlar mavjud emas</td>
                </tr>
              ) : (
                displayedLawyers.map((l) => (
                  <tr key={l.id} className="hover:bg-slate-50 transition-colors">
                    <td className="py-3 px-4">
                      <img
                        src={l.photoUrl}
                        alt={l.fullName}
                        className="w-10 h-10 rounded-full object-cover border border-slate-200 shadow-xs"
                      />
                    </td>
                    <td className="py-3 px-4">
                      <div className="font-semibold text-slate-900">{l.fullName}</div>
                      {l.education && (
                        <div className="text-[10px] text-slate-500 flex items-center mt-0.5 truncate max-w-xs">
                          <GraduationCap className="w-3 h-3 mr-1 text-[#c5a880] shrink-0" />
                          <span className="truncate">{l.education}</span>
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-slate-800 font-medium">{l.position}</div>
                      <div className="text-slate-500 text-[11px] truncate max-w-xs">{l.specialization}</div>
                    </td>
                    <td className="py-3 px-4 font-mono text-slate-700 whitespace-nowrap tabular-nums">
                      {l.experienceYears} yil
                    </td>
                    <td className="py-3 px-4 font-mono text-slate-700 font-medium whitespace-nowrap tabular-nums">
                      {l.phone ? formatUzPhone(l.phone) : '—'}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      {l.user ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200" title={`Login: @${l.user.username}`}>
                          <UserCheck className="w-3 h-3 mr-1 text-emerald-600" />
                          @{l.user.username}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] text-slate-400 bg-slate-100 border border-slate-200">
                          Ochilmagan
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => handleToggleActive(l)}
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold border transition-colors cursor-pointer ${
                          l.isActive
                            ? 'bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100'
                            : 'bg-slate-100 text-slate-500 border-slate-300 hover:bg-slate-200'
                        }`}
                        title={l.isActive ? "Bosh sahifadan olish uchun bosing" : "Bosh sahifaga qo‘yish uchun bosing"}
                      >
                        <Star className={`w-3 h-3 mr-1 ${l.isActive ? 'text-amber-500 fill-amber-400' : 'text-slate-400'}`} />
                        <span>{l.isActive ? 'Bosh sahifada faol' : 'Yashirilgan'}</span>
                      </button>
                    </td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      <div className="inline-flex items-center space-x-1.5">
                        <button
                          type="button"
                          onClick={() => handleToggleActive(l)}
                          className={`px-2 py-1 text-[11px] font-semibold rounded border transition-colors inline-flex items-center cursor-pointer shadow-2xs ${
                            l.isActive
                              ? 'bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100'
                              : 'bg-slate-50 text-slate-700 border-slate-300 hover:bg-slate-100'
                          }`}
                          title={l.isActive ? "Bosh sahifadan olish" : "Bosh sahifaga qo‘yish"}
                        >
                          <Star className={`w-3.5 h-3.5 mr-1 ${l.isActive ? 'text-amber-500 fill-amber-400' : 'text-slate-400'}`} />
                          <span>{l.isActive ? 'Bosh sahifadan olish' : 'Bosh sahifaga qo‘yish'}</span>
                        </button>
                        <button
                          onClick={() => handleOpenDossier(l)}
                          className="px-2 py-1 text-[11px] font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded transition-colors inline-flex items-center cursor-pointer shadow-2xs"
                          title="Dosye va Sud Kundaligi"
                        >
                          <Eye className="w-3.5 h-3.5 mr-1 text-[#c5a880]" />
                          Dosye & Kundalik
                        </button>
                        {!isLawyer && (
                          <button
                            onClick={() => handleOpenAccount(l)}
                            className="px-2 py-1 text-[11px] font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded transition-colors inline-flex items-center cursor-pointer shadow-2xs"
                            title="Shaxsiy kabinet akkaunti ochish yoki parolni yangilash"
                          >
                            <Key className="w-3.5 h-3.5 mr-1 text-blue-600" />
                            Akkaunt
                          </button>
                        )}
                        <button
                          onClick={() => handleOpenEdit(l)}
                          className="px-2 py-1 text-[11px] font-semibold text-slate-700 bg-slate-50 hover:bg-slate-100 hover:text-slate-900 border border-slate-300 rounded transition-colors inline-flex items-center cursor-pointer shadow-2xs"
                          title="Advokatni tahrirlash"
                          aria-label="Advokatni tahrirlash"
                        >
                          <Pencil className="w-3.5 h-3.5 text-slate-600 mr-1" />
                          <span>Tahrirlash</span>
                        </button>
                        {!isLawyer && (
                          <button
                            onClick={() => handleDelete(l.id, l.fullName)}
                            className="p-1.5 text-rose-500 hover:text-rose-700 hover:bg-rose-50 border border-transparent hover:border-rose-200 rounded transition-colors cursor-pointer"
                            title="O‘chirish"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ============================================================== */}
      {/* DOSSIER & COURT DIARY MODAL                                   */}
      {/* ============================================================== */}
      {dossierLawyer && (
        <Modal
          isOpen={isDossierOpen}
          onClose={() => setIsDossierOpen(false)}
          title={`Advokat Dosyesi: ${dossierLawyer.fullName}`}
          maxWidth="max-w-4xl"
        >
          <div className="space-y-5">
            {/* Tabs */}
            <div className="flex border-b border-slate-200 space-x-4">
              <button
                type="button"
                onClick={() => setDossierTab('INFO')}
                className={`pb-2.5 text-xs font-bold uppercase tracking-wider transition-colors border-b-2 cursor-pointer ${
                  dossierTab === 'INFO'
                    ? 'border-[#091322] text-[#091322]'
                    : 'border-transparent text-slate-400 hover:text-slate-600'
                }`}
              >
                Shaxsiy Dosye
              </button>
              <button
                type="button"
                onClick={() => setDossierTab('SCHEDULE')}
                className={`pb-2.5 text-xs font-bold uppercase tracking-wider transition-colors border-b-2 cursor-pointer flex items-center ${
                  dossierTab === 'SCHEDULE'
                    ? 'border-[#091322] text-[#091322]'
                    : 'border-transparent text-slate-400 hover:text-slate-600'
                }`}
              >
                <Calendar className="w-3.5 h-3.5 mr-1 text-[#c5a880]" />
                Bandlik & Sud Grafigi ({overrides.length})
              </button>
              <button
                type="button"
                onClick={() => setDossierTab('DIARY')}
                className={`pb-2.5 text-xs font-bold uppercase tracking-wider transition-colors border-b-2 cursor-pointer flex items-center ${
                  dossierTab === 'DIARY'
                    ? 'border-[#091322] text-[#091322]'
                    : 'border-transparent text-slate-400 hover:text-slate-600'
                }`}
              >
                <BookOpen className="w-3.5 h-3.5 mr-1 text-[#c5a880]" />
                Sud Kundaligi & Eslatmalar ({notes.length})
              </button>
            </div>

            {/* TAB 1: SHAXSIY DOSYE */}
            {dossierTab === 'INFO' && (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6 p-4 rounded-xl bg-slate-50 border border-slate-200">
                  <img
                    src={dossierLawyer.photoUrl}
                    alt={dossierLawyer.fullName}
                    className="w-24 h-24 rounded-2xl object-cover border-2 border-[#c5a880] shadow-md"
                  />
                  <div className="space-y-1">
                    <span className="text-[10px] uppercase font-bold tracking-wider text-[#c5a880] bg-[#091322] px-2 py-0.5 rounded">
                      {dossierLawyer.position}
                    </span>
                    <h3 className="font-serif text-2xl font-bold text-slate-900">
                      {dossierLawyer.fullName}
                    </h3>
                    <p className="text-xs text-slate-600 font-medium">
                      {dossierLawyer.specialization}
                    </p>
                    <div className="flex flex-wrap gap-4 pt-2 text-xs text-slate-600">
                      <span className="flex items-center">
                        <Phone className="w-3.5 h-3.5 mr-1 text-[#c5a880]" />
                        {dossierLawyer.phone ? formatUzPhone(dossierLawyer.phone) : '—'}
                      </span>
                      <span className="flex items-center">
                        <Mail className="w-3.5 h-3.5 mr-1 text-[#c5a880]" />
                        {dossierLawyer.email || '—'}
                      </span>
                      <span className="flex items-center font-mono">
                        <Clock className="w-3.5 h-3.5 mr-1 text-[#c5a880]" />
                        Tajriba: {dossierLawyer.experienceYears} yil
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                  <div className="p-4 rounded-lg border border-slate-200 bg-white">
                    <span className="font-semibold text-slate-700 block mb-1">Tarjimai hol (Biografiya):</span>
                    <p className="text-slate-600 leading-relaxed font-light">
                      {dossierLawyer.bioUz || 'Ma’lumot kiritilmagan.'}
                    </p>
                  </div>

                  <div className="p-4 rounded-lg border border-slate-200 bg-white">
                    <span className="font-semibold text-slate-700 block mb-1">Biriktirilgan amaliyot sohalari:</span>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {(dossierLawyer.services || []).map((s) => (
                        <span key={s.serviceId || s.service?.id} className="px-2 py-1 rounded bg-slate-100 text-slate-800 text-[11px] font-medium border border-slate-200">
                          {s.service?.titleUz || 'Xizmat'}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="p-4 rounded-lg border border-slate-200 bg-white">
                    <span className="font-semibold text-slate-700 block mb-1 flex items-center">
                      <GraduationCap className="w-3.5 h-3.5 mr-1 text-[#c5a880]" />
                      Oliy Ta’lim Muassasasi (Universitet yoki Institut):
                    </span>
                    <p className="text-slate-600 leading-relaxed font-light">
                      {dossierLawyer.education || 'Toshkent davlat yuridik universiteti (TDYuU)'}
                    </p>
                  </div>

                  <div className="p-4 rounded-lg border border-slate-200 bg-white">
                    <span className="font-semibold text-slate-700 block mb-1 flex items-center">
                      <Award className="w-3.5 h-3.5 mr-1 text-[#c5a880]" />
                      Litsenziya va Sertifikatlar:
                    </span>
                    <p className="text-slate-600 leading-relaxed font-light">
                      {dossierLawyer.certifications || 'O‘zbekiston Advokatlar Palatasi a’zosi va davlat litsenziyasi'}
                    </p>
                  </div>

                  {dossierLawyer.user && (
                    <div className="p-4 rounded-lg border border-emerald-200 bg-emerald-50/50 md:col-span-2 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Key className="w-4 h-4 text-emerald-600 shrink-0" />
                        <div>
                          <span className="text-xs font-semibold text-emerald-900 block">
                            Advokat Shaxsiy Portali Akkaunti Faol
                          </span>
                          <span className="text-[11px] text-emerald-700 font-mono">
                            Login: @{dossierLawyer.user.username} | Email: {dossierLawyer.user.email}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setIsDossierOpen(false);
                          handleOpenAccount(dossierLawyer);
                        }}
                        className="px-2.5 py-1 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded cursor-pointer"
                      >
                        Parolni yangilash
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* TAB 2: SCHEDULE OVERRIDES (Bo'sh, Sudi bor, Ishi bor, Dam olish) */}
            {dossierTab === 'SCHEDULE' && (
              <div className="space-y-5">
                {/* Add override form */}
                <form onSubmit={handleSaveScheduleOverride} className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                    Sana bo‘yicha grafik va sud holatini belgilash
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-[11px] text-slate-600 mb-1">Sana:</label>
                      <input
                        type="date"
                        required
                        value={newOverrideDate}
                        onChange={(e) => setNewOverrideDate(e.target.value)}
                        className="w-full px-3 py-1.5 border border-slate-300 rounded text-xs bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-slate-600 mb-1">Holat:</label>
                      <select
                        value={newOverrideStatus}
                        onChange={(e) => setNewOverrideStatus(e.target.value)}
                        className="w-full px-3 py-1.5 border border-slate-300 rounded text-xs bg-white font-semibold"
                      >
                        <option value="COURT">Sudi bor (Sud majlisi)</option>
                        <option value="BUSY">Ishi bor (Band)</option>
                        <option value="OFF">Dam olish kuni</option>
                        <option value="FREE">Bo‘sh (Qabul ochiq)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] text-slate-600 mb-1">Sabab / Sud joyi:</label>
                      <input
                        type="text"
                        placeholder="Masalan: Jizzax viloyat sudi"
                        value={newOverrideReason}
                        onChange={(e) => setNewOverrideReason(e.target.value)}
                        className="w-full px-3 py-1.5 border border-slate-300 rounded text-xs bg-white"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-1">
                    <button
                      type="submit"
                      disabled={savingOverride}
                      className="px-4 py-2 bg-[#091322] hover:bg-[#14233c] text-[#c5a880] rounded text-xs font-semibold cursor-pointer disabled:opacity-50"
                    >
                      {savingOverride ? 'Saqlanmoqda...' : 'Holatni saqlash'}
                    </button>
                  </div>
                </form>

                {/* Overrides Table */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase border-b border-slate-200">
                      <tr>
                        <th className="py-2.5 px-3">Sana</th>
                        <th className="py-2.5 px-3">Holat</th>
                        <th className="py-2.5 px-3">Izoh / Sud joyi</th>
                        <th className="py-2.5 px-3 text-right">O‘chirish</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {overrides.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center py-6 text-slate-400">
                            Maxsus kiritilgan grafik holatlari yo‘q (Standart rejimda ishlaydi)
                          </td>
                        </tr>
                      ) : (
                        overrides.map((ov) => {
                          const badge = getStatusBadgeProps(ov.status);
                          return (
                            <tr key={ov.id} className="hover:bg-slate-50">
                              <td className="py-2 px-3 font-mono font-semibold text-slate-800">
                                {ov.date}
                              </td>
                              <td className="py-2 px-3">
                                <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold border ${badge.bg}`}>
                                  {badge.label}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-slate-700">
                                {ov.reason || '—'}
                              </td>
                              <td className="py-2 px-3 text-right">
                                <button
                                  type="button"
                                  onClick={() => handleDeleteOverride(ov.id)}
                                  className="text-rose-600 hover:text-rose-800 p-1"
                                  title="O‘chirish"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TAB 3: SUD KUNDALIGI (Qaerda, Qachon, Kim bilan, Tafsilotlar) */}
            {dossierTab === 'DIARY' && (
              <div className="space-y-5">
                {/* Add Note Form */}
                <form onSubmit={handleSaveNote} className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800 flex items-center">
                    <BookOpen className="w-4 h-4 mr-1.5 text-[#c5a880]" />
                    Yangi Sud Majlisi yoki Yuridik Eslatma Qo‘shish
                  </h4>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] text-slate-600 mb-1">Mavzu / Ish nomi *:</label>
                      <input
                        type="text"
                        required
                        placeholder="Masalan: MChJ da’vo ishi bo‘yicha sud majlisi"
                        value={newNoteTitle}
                        onChange={(e) => setNewNoteTitle(e.target.value)}
                        className="w-full px-3 py-1.5 border border-slate-300 rounded text-xs bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-slate-600 mb-1">Qaerda (Sud / Organ manzili):</label>
                      <input
                        type="text"
                        placeholder="Masalan: Jizzax tumanlararo iqtisodiy sudi, 2-zal"
                        value={newNoteLocation}
                        onChange={(e) => setNewNoteLocation(e.target.value)}
                        className="w-full px-3 py-1.5 border border-slate-300 rounded text-xs bg-white"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-[11px] text-slate-600 mb-1">Sana:</label>
                      <input
                        type="date"
                        value={newNoteDate}
                        onChange={(e) => setNewNoteDate(e.target.value)}
                        className="w-full px-3 py-1.5 border border-slate-300 rounded text-xs bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-slate-600 mb-1">Boshlanish vaqti (Dan):</label>
                      <input
                        type="time"
                        value={newNoteStartTime}
                        onChange={(e) => setNewNoteStartTime(e.target.value)}
                        className="w-full px-3 py-1.5 border border-slate-300 rounded text-xs bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-slate-600 mb-1">Tugash vaqti (Gacha):</label>
                      <input
                        type="time"
                        value={newNoteEndTime}
                        onChange={(e) => setNewNoteEndTime(e.target.value)}
                        className="w-full px-3 py-1.5 border border-slate-300 rounded text-xs bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-slate-600 mb-1">Kim bilan (Sudya, mijoz):</label>
                      <input
                        type="text"
                        placeholder="Masalan: Sudya A.Rahmonov"
                        value={newNoteParties}
                        onChange={(e) => setNewNoteParties(e.target.value)}
                        className="w-full px-3 py-1.5 border border-slate-300 rounded text-xs bg-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] text-slate-600 mb-1">Batafsil ma’lumot va eslatmalar *:</label>
                    <textarea
                      required
                      rows={2}
                      placeholder="Ish holati, kiritiladigan iltimosnomalar, talab qilinadigan hujjatlar..."
                      value={newNoteContent}
                      onChange={(e) => setNewNoteContent(e.target.value)}
                      className="w-full px-3 py-1.5 border border-slate-300 rounded text-xs bg-white resize-none"
                    />
                  </div>

                  <div className="flex justify-end pt-1">
                    <button
                      type="submit"
                      disabled={savingNote}
                      className="px-4 py-2 bg-[#091322] hover:bg-[#14233c] text-[#c5a880] rounded text-xs font-semibold cursor-pointer disabled:opacity-50"
                    >
                      {savingNote ? 'Saqlanmoqda...' : 'Daftarchaga kiritish'}
                    </button>
                  </div>
                </form>

                {/* Notes List */}
                <div className="space-y-2.5">
                  {notes.length === 0 ? (
                    <div className="p-8 text-center text-slate-400 border border-slate-200 rounded-lg">
                      Hozircha sud eslatmalari mavjud emas
                    </div>
                  ) : (
                    notes.map((n) => (
                      <div
                        key={n.id}
                        className={`p-4 rounded-lg border transition-all ${
                          n.isDone
                            ? 'bg-slate-50 border-slate-200 opacity-75'
                            : 'bg-white border-amber-200 shadow-xs'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2">
                              <button
                                type="button"
                                onClick={() => handleToggleNoteDone(n.id, n.isDone)}
                                className={`w-4 h-4 rounded border flex items-center justify-center cursor-pointer ${
                                  n.isDone ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-slate-300'
                                }`}
                              >
                                {n.isDone && <Check className="w-3 h-3" />}
                              </button>
                              <h5 className={`font-semibold text-xs ${n.isDone ? 'line-through text-slate-500' : 'text-slate-900'}`}>
                                {n.title}
                              </h5>
                              <span className="text-[10px] font-mono text-[#c5a880] bg-[#091322] px-1.5 py-0.2 rounded">
                                {n.eventDate} {n.eventTime ? (n.eventTime.includes('dan') ? n.eventTime : `soat ${n.eventTime}`) : ''}
                              </span>
                            </div>

                            {n.location && (
                              <p className="text-[11px] text-slate-600 flex items-center">
                                <MapPin className="w-3 h-3 mr-1 text-[#c5a880]" />
                                Manzil: {n.location}
                              </p>
                            )}

                            {n.parties && (
                              <p className="text-[11px] text-slate-600 flex items-center">
                                <Users className="w-3 h-3 mr-1 text-slate-400" />
                                Ishtirokchilar: {n.parties}
                              </p>
                            )}

                            <p className="text-xs text-slate-700 pt-1 font-light italic">
                              "{n.content}"
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={() => handleDeleteNote(n.id)}
                            className="text-slate-400 hover:text-rose-600 p-1"
                            title="O‘chirish"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

          </div>
        </Modal>
      )}

      {/* ============================================================== */}
      {/* ADD / EDIT LAWYER PROFILE MODAL                               */}
      {/* ============================================================== */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingLawyer ? `Advokatni Tahrirlash: ${editingLawyer.fullName}` : 'Yangi Advokat Qo‘shish'}
        maxWidth="max-w-3xl"
      >
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                To‘liq Ism-Familiya *
              </label>
              <input
                type="text"
                required
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: capitalizeWords(e.target.value) })}
                className="w-full px-3 py-2 border border-slate-300 rounded text-xs focus:outline-none focus:border-[#c5a880]"
                placeholder="Masalan: Jamshid Karimov"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                Lavozimi *
              </label>
              <input
                type="text"
                required
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded text-xs focus:outline-none focus:border-[#c5a880]"
                placeholder="Advokat"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                Ixtisoslashuv
              </label>
              <input
                type="text"
                value={formData.specialization}
                onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded text-xs focus:outline-none focus:border-[#c5a880]"
                placeholder="Jinoyat ishlari, Oila huquqi"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                Tajriba (Yil) *
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="1"
                  max="60"
                  placeholder="Yil"
                  value={formData.experienceYears === '' ? '' : formData.experienceYears}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData({ ...formData, experienceYears: val === '' ? '' : parseInt(val, 10) });
                  }}
                  className="w-full px-3 py-2 border border-slate-300 rounded text-xs focus:outline-none focus:border-[#c5a880]"
                />
                <select
                  value={formData.experienceYears || ''}
                  onChange={(e) => setFormData({ ...formData, experienceYears: e.target.value ? parseInt(e.target.value, 10) : '' })}
                  className="px-2 py-2 border border-slate-300 rounded text-xs bg-slate-50 text-slate-700 cursor-pointer"
                >
                  <option value="">Tanlash...</option>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 18, 20, 25, 30, 35, 40].map((y) => (
                    <option key={y} value={y}>{y} yil</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                Telefon Raqami
              </label>
              <input
                type="text"
                value={formData.phone || '+998 ()'}
                onFocus={() => {
                  if (!formData.phone) setFormData((p) => ({ ...p, phone: '+998 ()' }));
                }}
                onChange={(e) => setFormData({ ...formData, phone: formatUzPhone(e.target.value, formData.phone) })}
                placeholder="+998 (97) 325-07-70"
                className="w-full px-3 py-2 border border-slate-300 rounded text-xs font-mono focus:outline-none focus:border-[#c5a880]"
              />
            </div>
          </div>

          {/* Fotosurat yuklash (Tiniq formatda) */}
          <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
            <label className="block text-xs font-semibold text-slate-800 uppercase tracking-wider">
              Fotosurat yuklash (Tiniq formatda) *
            </label>
            <div className="flex items-start space-x-4">
              {formData.photoUrl ? (
                <div className="relative w-24 h-28 rounded-lg overflow-hidden border-2 border-[#c5a880] shadow bg-slate-100 shrink-0">
                  <img src={formData.photoUrl} alt="Preview" className="w-full h-full object-cover object-top" />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, photoUrl: '' })}
                    className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-0.5 hover:bg-red-700 text-xs cursor-pointer shadow"
                    title="Rasmni o‘chirish"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="w-24 h-28 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 text-xs shrink-0 bg-white">
                  <Upload className="w-6 h-6 mb-1 text-slate-400" />
                  <span>Rasm yo‘q</span>
                </div>
              )}

              <div className="flex-1 space-y-2">
                <label className="inline-flex items-center px-4 py-2 border border-slate-300 rounded-md shadow-sm text-xs font-medium text-slate-700 bg-white hover:bg-slate-50 cursor-pointer">
                  <Upload className="w-4 h-4 mr-2 text-[#c5a880]" />
                  <span>{uploadingImage ? 'Yuklanmoqda...' : 'Kompyuterdan rasm tanlash...'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                    className="hidden"
                  />
                </label>
                <p className="text-[11px] text-slate-500">
                  Tavsiya etiladi: Yuqori aniqlikdagi (HD) portret fotosurat (JPG, PNG, WEBP). Maksimal: 10 MB.
                </p>
                {formData.photoUrl && (
                  <div className="flex items-center text-[11px] text-emerald-700 font-semibold bg-emerald-50 px-2.5 py-1 rounded border border-emerald-200 w-fit">
                    ✓ Fotosurat muvaffaqiyatli tanlandi va yuklandi
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Telegram Akkount */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
              Telegram Akkount (Profil yoki Havola)
            </label>
            <input
              type="text"
              value={formData.telegram}
              onChange={(e) => setFormData({ ...formData, telegram: e.target.value })}
              placeholder="@advokat yoki https://t.me/..."
              className="w-full px-3 py-2 border border-slate-300 rounded text-xs focus:outline-none focus:border-[#c5a880]"
            />
          </div>

          {/* Oliy Ta’lim & Litsenziyalar - Bir xil va to‘g‘ri chiziqli dizayn */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1 flex items-center h-5">
                <GraduationCap className="w-4 h-4 mr-1.5 text-[#c5a880] shrink-0" />
                <span>Oliy Ta’lim Muassasasi *</span>
              </label>
              <input
                type="text"
                required
                value={formData.education}
                onChange={(e) => setFormData({ ...formData, education: e.target.value })}
                placeholder="Masalan: TDYuU (Toshkent davlat yuridik universiteti)"
                className="w-full px-3 py-2 border border-slate-300 rounded text-xs focus:outline-none focus:border-[#c5a880]"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Tugatgan universiteti yoki instituti (TDYuU, JDPU huquq fakulteti va h.k.)
              </p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1 flex items-center h-5">
                <Award className="w-4 h-4 mr-1.5 text-[#c5a880] shrink-0" />
                <span>Litsenziya va Sertifikatlar</span>
              </label>
              <input
                type="text"
                value={formData.certifications}
                onChange={(e) => setFormData({ ...formData, certifications: e.target.value })}
                placeholder="Masalan: O‘zbekiston Advokatlar Palatasi litsenziyasi № 002456"
                className="w-full px-3 py-2 border border-slate-300 rounded text-xs focus:outline-none focus:border-[#c5a880]"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Advokatlik litsenziyasi va kasbiy guvohnoma ma’lumotlari
              </p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
              Biriktirilgan amaliyot sohalari:
            </label>
            <div className="flex flex-wrap gap-2 pt-1">
              {services.map((s) => {
                const selected = formData.serviceIds.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => toggleService(s.id)}
                    className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors cursor-pointer ${
                      selected
                        ? 'bg-[#091322] text-[#c5a880] border-[#091322]'
                        : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    {s.titleUz}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
              Biografiya / Malaka (O‘zbekcha):
            </label>
            <textarea
              rows={3}
              value={formData.bioUz}
              onChange={(e) => setFormData({ ...formData, bioUz: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded text-xs focus:outline-none focus:border-[#c5a880]"
            />
          </div>

          <div className="flex items-center space-x-2 pt-2 p-2.5 rounded bg-amber-50/70 border border-amber-200">
            <input
              type="checkbox"
              id="lawyerActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="w-4 h-4 rounded text-[#c5a880] focus:ring-[#c5a880] cursor-pointer"
            />
            <label htmlFor="lawyerActive" className="text-xs font-semibold text-amber-950 cursor-pointer flex items-center">
              <Star className="w-3.5 h-3.5 mr-1.5 text-amber-500 fill-amber-400 shrink-0" />
              <span>Bosh sahifaga qo‘yish (Asosiy sahifada va qabul ro‘yxatida faol ko‘rinsin)</span>
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 border border-slate-300 rounded text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              Bekor qilish
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 bg-[#091322] hover:bg-[#14233c] text-[#c5a880] rounded text-xs font-semibold cursor-pointer disabled:opacity-50"
            >
              {saving ? 'Saqlanmoqda...' : 'Saqlash'}
            </button>
          </div>
        </form>
      </Modal>

      {/* ============================================================== */}
      {/* LAWYER SPECIALIZED ACCOUNT PROVISIONING MODAL                 */}
      {/* ============================================================== */}
      <Modal
        isOpen={isAccountModalOpen}
        onClose={() => setIsAccountModalOpen(false)}
        title={`Advokat Shaxsiy Akkaunti: ${accountLawyer?.fullName || ''}`}
        maxWidth="max-w-md"
      >
        <form onSubmit={handleSaveAccount} className="space-y-4 text-xs">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-900 leading-relaxed">
            <p className="font-semibold flex items-center mb-1">
              <Key className="w-3.5 h-3.5 mr-1 text-blue-600" />
              Ixtisoslashgan advokat kabineti
            </p>
            <p className="text-[11px] text-blue-700 font-light">
              Ushbu login va parol orqali tizimga kirganida, portal to‘liq ushbu advokatga ixtisoslashadi (faqat o‘zining qabullari, sudi, taqvimi va statistikasi ko‘rinadi).
            </p>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 uppercase mb-1">
              Login (Username) *
            </label>
            <input
              type="text"
              required
              value={accountForm.username}
              onChange={(e) => setAccountForm({ ...accountForm, username: e.target.value.toLowerCase().trim() })}
              placeholder="masalan: jamshid.karimov"
              className="w-full px-3 py-2 border rounded font-mono focus:border-[#c5a880]"
            />
          </div>


          <div>
            <label className="block font-semibold text-slate-700 uppercase mb-1">
              {(accountLawyer?.user || accountLawyer?.userId) ? 'Yangi Parol (Agar o‘zgartirmoqchi bo‘lsangiz)' : 'Parol (Kamida 6 ta belgi) *'}
            </label>
            <PasswordInput
              required={!(accountLawyer?.user || accountLawyer?.userId)}
              minLength={6}
              value={accountForm.password}
              onChange={(e) => setAccountForm({ ...accountForm, password: e.target.value })}
              placeholder="Kamida 6 ta belgi..."
              className="w-full px-3 py-2 border rounded font-mono focus:border-[#c5a880]"
            />
          </div>

          <div>
            <label className="block font-semibold text-slate-700 uppercase mb-1">
              Telefon raqami
            </label>
            <input
              type="text"
              value={accountForm.phone || '+998 ()'}
              onFocus={() => {
                if (!accountForm.phone) setAccountForm((p) => ({ ...p, phone: '+998 ()' }));
              }}
              onChange={(e) => setAccountForm({ ...accountForm, phone: formatUzPhone(e.target.value, accountForm.phone) })}
              placeholder="+998 (97) 325-07-70"
              className="w-full px-3 py-2 border rounded font-mono focus:border-[#c5a880]"
            />
          </div>

          <div className="pt-3 border-t flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setIsAccountModalOpen(false)}
              className="px-4 py-2 border rounded text-slate-700 hover:bg-slate-50 cursor-pointer"
            >
              Bekor qilish
            </button>
            <button
              type="submit"
              disabled={savingAccount}
              className="px-5 py-2 bg-[#091322] text-[#c5a880] font-semibold rounded hover:bg-[#15243d] cursor-pointer disabled:opacity-50"
            >
              {savingAccount ? 'Saqlanmoqda...' : (accountLawyer?.user || accountLawyer?.userId) ? 'Akkauntni yangilash' : 'Akkaunt yaratish'}
            </button>
          </div>
        </form>
      </Modal>

    </div>
  );
}
